import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import type { SiteEntity } from '../../types/site';
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteComposite } from '../../types/portfolioComposite';
import { canonicalFromVerdictWord } from '../reportVerdict';
import { stripConfidenceComments } from '../reportRendering/narrativeArtifacts';
import { reportReaderPath } from '../siteNavigation';

export interface SavedScreeningReportContext {
  reportId: string;
  label: string;
  href: string;
  linkNotice: string;
  date: string | null;
  revisionId: string | null;
  currency: string;
  notes: string[];
}

const text = (value: unknown): string | null => typeof value === 'string' && value.trim() ? value.trim() : null;
const positive = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;
const mw = (value: number): string => value.toLocaleString('en-US', { maximumFractionDigits: 2 });
// Legacy deep-dive operands are strings. Reject ranges, estimates and prose;
// only a complete single MW quantity can support arithmetic within that row.
const exactMW = (value: unknown): number | null => {
  if (typeof value !== 'string' || !/^\s*\d+(?:\.\d+)?\s*MW\s*$/i.test(value)) return null;
  const number = Number(value.trim().replace(/\s*MW$/i, ''));
  return Number.isFinite(number) && number >= 0 ? number : null;
};

/** A display-only disagreement check across all recognized saved carriers.
 * It deliberately does not change the shared verdict reconciliation policy. */
function savedDecisionDisagreement(report: SiteRagReport): string | null {
  if ((report.reportType ?? 'feasibility') !== 'feasibility') return null;
  const band = text(report.decisionAnalysis?.semantic?.systemDecision?.band);
  const recommendation = text(report.conclusion?.recommendation);
  const status = text(report.overallStatus);
  const canonicalStatus = ({ RED: 'stop', NO_GO: 'stop', AMBER: 'caution', CONDITIONAL: 'caution', GREEN: 'proceed', GO: 'proceed', WITHHELD: 'inconclusive' } as Record<string, string>)[status?.toUpperCase() ?? ''];
  const fields = [
    { name: 'system decision', value: band, canonical: canonicalFromVerdictWord(band) },
    { name: 'conclusion', value: recommendation, canonical: recommendation === 'Decision Pending' ? 'inconclusive' : canonicalFromVerdictWord(recommendation) },
    { name: 'overall status', value: status, canonical: canonicalStatus },
  ].filter(field => field.canonical != null);
  return new Set(fields.map(field => field.canonical)).size > 1
    ? `Saved decision fields disagree (${fields.map(field => `${field.name}: ${field.value}`).join('; ')}). The saved screening classification is unchanged. Reconcile these recorded claims before relying on them; modeled adequacy is not development clearance.`
    : null;
}

/** Legacy per-phase rows already pair their own load and supply operands.
 * Never join the site-wide minimum firm scalar to the worst phase label. */
function legacyPhaseNotes(report: SiteRagReport): string[] {
  const deepDive = report.powerAnalysis?.capacityCreditDeepDive;
  const phases = Array.isArray(deepDive?.perPhase) ? deepDive.perPhase : [];
  return phases.flatMap(phase => {
    const label = text(phase?.phaseLabel);
    const facility = text(phase?.facilityLoadMW);
    const firm = text(phase?.firmDeliverableMW);
    const required = text(phase?.requiredFirmMW);
    if (!label || !facility || !firm || !required) return [];
    // Duplicate labels cannot establish which phase a comparison belongs to.
    if (phases.filter(other => text(other?.phaseLabel) === label).length !== 1) return [];
    const firmMW = exactMW(firm), requiredMW = exactMW(required);
    const deficit = firmMW != null && requiredMW != null && requiredMW > firmMW
      ? `; ${mw(requiredMW - firmMW)} MW shortfall` : '';
    const notes = [`Saved planning model — ${label}: ${firm} modeled firm capacity against ${required} required${deficit}. Recorded facility load basis: ${stripConfidenceComments(facility)}. Planning-default capacity credits are not a site-specific study or a firm supply commitment.`];
    if (/midpoint|range|sizing basis/i.test(facility)) notes.push(`The recorded ${label} load basis is a range or sizing assumption. Adequacy at this sizing basis does not establish adequacy across the full load range.`);
    return notes;
  });
}

/** Read saved context only. This neither re-scores a report nor establishes the
 * cause/currentness of a stage outcome: composite IDs name pillar contributors,
 * and the stage may also use other reports or a different intent snapshot. */
export function savedScreeningReportContexts(
  site: SiteEntity,
  composite: SiteComposite,
  reports: readonly SiteRagReport[],
): SavedScreeningReportContext[] {
  const required = new Set(composite.decisionStages?.screening.requiredTypes ?? []);
  return [...new Set(Array.isArray(composite.sourceReportIds) ? composite.sourceReportIds : [])].flatMap(id => {
    // The existing reader resolves by report ID. Do not link when any loaded
    // owner/site supplies another row with that ID, even if one matches here.
    if (reports.filter(report => report.id === id).length !== 1) return [];
    const matches = reports.filter(report => report.id === id
      && (report.siteId === site.id || (!report.siteId && site.reportIds?.includes(id)))
      && required.has((report.reportType ?? 'feasibility') as ReportTypeId));
    // An ambiguous identity cannot supply a source link or an explanation.
    if (matches.length !== 1) return [];
    const report = matches[0]!;
    const type = (report.reportType ?? 'feasibility') as ReportTypeId;
    const version = positive(report.version)
      ? ` v${report.version}${typeof report.minorVersion === 'number' && Number.isSafeInteger(report.minorVersion) && report.minorVersion >= 0 ? `.${report.minorVersion}` : ''}` : '';
    const sedDiffers = (site.sedId != null && report.sedId != null && site.sedId !== report.sedId)
      || (site.sedVersion != null && report.sedVersionUsed != null && site.sedVersion !== report.sedVersionUsed);
    const context: SavedScreeningReportContext = {
      reportId: id,
      label: `${REPORT_TYPE_REGISTRY[type]?.shortLabel ?? 'Screening report'}${version}`,
      href: reportReaderPath(encodeURIComponent(site.id), encodeURIComponent(id)),
      linkNotice: 'Opens the current report by ID; this link does not pin its owner or immutable revision. The reader may differ from this saved context. Confirm the report identity and revision before relying on it.',
      date: text(report.createdAt) && Number.isFinite(Date.parse(report.createdAt)) ? report.createdAt : null,
      revisionId: text(report.reportRevision?.revisionId),
      currency: report.requiresRegeneration != null
        ? 'Saved report is marked for refresh; review its currentness before decision use.'
        : sedDiffers
          ? 'Saved and current SED identities differ; material input currentness is not established here.'
          : 'Currentness and decision approval are not established by this saved context.',
      notes: [],
    };
    if (report._metadataOnly || (report as SiteRagReport & { _portfolioSlimBody?: boolean })._portfolioSlimBody
      || typeof report.summary !== 'string') {
      context.notes.push('Report body is not loaded; source reasons are unavailable here.');
      return [context];
    }

    const conflict = savedDecisionDisagreement(report);
    if (conflict) context.notes.push(conflict);
    const rationale = text(report.decisionAnalysis?.semantic?.systemDecision?.rationale);
    if (rationale) context.notes.push(`Recorded decision rationale: ${rationale}`);
    const conditions = (Array.isArray(report.conclusion?.keyConditions) ? report.conclusion.keyConditions : []).filter((value): value is string => text(value) !== null);
    for (const condition of conditions.slice(0, 3)) context.notes.push(`Recorded condition: ${condition}`);
    const actions = (Array.isArray(report.conclusion?.nextActions) ? report.conclusion.nextActions : []).filter((value): value is string => text(value) !== null);
    for (const action of actions.slice(0, 3)) context.notes.push(`Recorded next action (saved recommendation, not approval or independently verified evidence): ${stripConfidenceComments(action)}`);
    const drivers = (Array.isArray(report.verdictLean?.drivers) ? report.verdictLean.drivers : []).filter((value): value is string => text(value) !== null);
    if (drivers.length) context.notes.push(`Recorded narrative drivers: ${drivers.slice(0, 3).join(' ')}`);
    const permit = text(report.waterAnalysis?.consumptiveUse?.withdrawalPermit);
    if (permit) context.notes.push(`Recorded withdrawal-permit finding: ${permit}. Verify its applicable source and effective date; the report date alone does not establish either.`);

    // This is the persisted, matched phase comparison. Do not join an old
    // site-wide minimum to a different phase, recompute against live intent,
    // or imply that a midpoint validates a whole declared load range.
    const capacity = report.firmCapacityVerdict;
    const phase = capacity?.phaseComparison;
    if (phase?.source === 'phase-feasibility-v1' && text(phase.phaseLabel)
      && positive(phase.requiredMW) && positive(phase.requiredFirmMW)
      && typeof phase.firmDeliverableMW === 'number' && Number.isFinite(phase.firmDeliverableMW) && phase.firmDeliverableMW >= 0) {
      const deficit = Math.max(0, phase.requiredFirmMW - phase.firmDeliverableMW);
      context.notes.push(`Saved planning model — ${phase.phaseLabel}: ${mw(phase.firmDeliverableMW)} MW modeled firm capacity against ${mw(phase.requiredFirmMW)} MW required${deficit > 0 ? `; ${mw(deficit)} MW shortfall` : ''}. Planning-default capacity credits are not a site-specific study or a firm supply commitment.`);
      if (phase.loadBasis === 'bucket-sizing-basis') {
        context.notes.push(`Model load basis: ${text(phase.loadBasisLabel) ?? 'bucket sizing basis; detailed basis unavailable'}. Adequacy at this sizing basis does not establish adequacy across the full load range.`);
      }
      const rangeSources = (Array.isArray(phase.sources) ? phase.sources : []).filter(source => source?.capacityBasis === 'range-midpoint');
      if (rangeSources.length) {
        const ranges = rangeSources.map(source => {
          const range = source.capacityRange;
          return range && positive(range.min) && positive(range.max) && range.max >= range.min
            ? `${source.component}: ${mw(range.min)}–${mw(range.max)} MW` : `${source.component}: range endpoints unavailable`;
        });
        context.notes.push(`Source capacities use range midpoints (${ranges.join('; ')}). The saved comparison does not establish adequacy at every source-range endpoint.`);
      }
    } else {
      const legacy = legacyPhaseNotes(report);
      context.notes.push(...legacy);
      if (!legacy.length && (capacity?.worstVerdict === 'infeasible' || capacity?.worstVerdict === 'marginal')) {
        context.notes.push('The saved planning model records a firm-capacity constraint; a matched phase comparison is unavailable. This is not a verified permanent site disqualifier.');
      }
    }
    if (context.notes.length === 0) context.notes.push('No specific source reason is available in this saved context. A score or red classification alone does not establish a verified site disqualifier.');
    return [context];
  });
}
